<template>
    <v-dialog v-model="show" max-width="440" persistent>
        <v-card rounded="lg" class="pa-2">
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">Share live location</span>
                <v-btn icon="mdi-close" size="small" variant="text" @click="close"/>
            </v-card-title>
            <v-card-text>
                <p v-if="!blockedByPlan" class="text-body-2 text-medium-emphasis mb-3">
                    Anyone with this link can see <strong>{{ name || 'this tracker' }}</strong>'s live location
                    for about an hour. You can stop sharing any time.
                </p>
                <div v-if="loading" class="text-center py-4">
                    <v-progress-circular color="primary" indeterminate size="24"/>
                </div>
                <template v-else-if="token">
                    <v-text-field :model-value="link" readonly density="compact" variant="outlined" hide-details
                                  append-inner-icon="mdi-content-copy" @click:append-inner="copy"/>
                    <p class="text-caption text-medium-emphasis mt-1">Expires around {{ expiresText }}.</p>
                </template>
                <v-alert v-if="error" :type="blockedByPlan ? 'info' : 'error'" variant="tonal" density="compact"
                         class="mt-2">{{ error }}
                </v-alert>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
                <v-btn v-if="token" color="error" variant="text" :loading="revoking" @click="revoke">Stop sharing</v-btn>
                <v-spacer/>
                <v-btn v-if="canUpgradePlan" color="primary" variant="flat" @click="goToPlans">See plans</v-btn>
                <v-btn v-if="token" color="primary" variant="flat" @click="copy">Copy link</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useClipboard} from "@/composables/clipboard";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";
import {apiErrorMessage} from "@/composables/useApiError";

const props = defineProps({
    deviceId: {type: [Number, String], required: true},
    name: {type: String, default: ''},
});
const emits = defineEmits(['close']);

const show = ref(true);
const loading = ref(true);
const revoking = ref(false);
const shareId = ref(null);
const token = ref(null);
const expiresAt = ref(null);
const error = ref('');
const blockedByPlan = ref(false);
// Snapshotted with the message, not read live. error.value is written once in
// the catch; if ownership resolved a moment later, a live computed would show
// the See plans button next to text that still says "ask your circle owner".
const errorCanUpgrade = ref(false);
const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();
const router = useRouter();
const auth = useAuthStore();

// Only the circle owner can change the plan, and /plans is ownerOnly, so
// offering a member the button would bounce them straight back out.
const canUpgradePlan = computed(() => blockedByPlan.value && errorCanUpgrade.value);
const goToPlans = () => {
    close();
    router.push({name: 'plans'});
};

const link = computed(() => token.value ? `${window.location.origin}/share/${token.value}` : '');
const expiresText = computed(() => {
    if (!expiresAt.value) return 'an hour from now';
    return new Date(expiresAt.value).toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
});

onMounted(() => {
    send('post', `personal-trackers/${props.deviceId}/share`, {})
        .then((r) => {
            const d = r.data?.data;
            shareId.value = d?.id;
            token.value = d?.token;
            expiresAt.value = d?.expires_at;
        })
        .catch((e) => {
            // "Please try again" was wrong for the one refusal that never
            // changes: sharing is a premium feature, so on a 402 the customer
            // needs to know what to do instead of retrying forever.
            const canUpgrade = auth.isCircleOwner;
            const info = apiErrorMessage(e, "Couldn't create a share link. Please try again.", {canUpgrade});
            blockedByPlan.value = info.isUpgradeRequired;
            errorCanUpgrade.value = canUpgrade;
            error.value = info.message;
        })
        .finally(() => loading.value = false);
});

const copy = () => {
    if (!link.value) return;
    copyToClipboard(link.value);
    showSnackbar('Link copied');
};
const revoke = () => {
    if (!shareId.value) return;
    revoking.value = true;
    send('delete', `personal-trackers/shares/${shareId.value}`)
        .then(() => { showSnackbar('Sharing stopped'); close(); })
        .catch(() => { error.value = "Couldn't stop sharing. Please try again."; })
        .finally(() => revoking.value = false);
};
const close = () => {
    show.value = false;
    emits('close');
};
</script>
